import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { city, country } from '../../slices/settingsSlice';
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { validateNoStartOrEndSpace } from '../../cutomValidationField';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { showPromiseNotification } from '../../common/notification';
import { useNavigate } from 'react-router-dom';
import { completeUserProfile } from '../../slices/authSlice';
import Select from 'react-select';
import { reactSelectStyles } from '../../common/customCss';
import { useTranslation } from 'react-i18next';
import '../../i18';
import { uploadFileToS3 } from '../../common/uploadToS3';

export const CompleteProfile = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const fileRef = useRef(null);
    const fileCertificateRef = useRef(null);

    const [imagePreview, setImagePreview] = useState(null);
    const [govCertificatePreview, setGovCertificatePreview] = useState(null);
    const [govCertiImgError, setGovCertiImgError] = useState('');

    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [file, setFile] = useState('');
    const [govCertificate, setGovCertificate] = useState('')

    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [answer, setAnswer] = useState('');
    const dispatch = useDispatch();


    const countries = useSelector((state) => state.settings.countryListing);
    const cities = useSelector((state) => state.settings.cityList);
    const userInfo = useSelector((state) => state.auth.userInfo);

    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 18);

    useEffect(() => {
        if (userInfo?.step === 2) {
            navigate("/home")
        }
    }, [userInfo])

    useEffect(() => {
        dispatch(country({ limit: 500, sort: "name" }));
    }, [dispatch]);

    useEffect(() => {
        if (selectedCountry) {
            dispatch(city({ limit: 10000, sort: "name", country_id: selectedCountry }));
        }
        else {
            setSelectedCity("");
        }
    }, [dispatch, selectedCountry]);

    const handleCountryChange = (data) => {
        const value = data.value;
        if (value !== selectedCountry) {
            setSelectedCountry(value);
            setSelectedCity(""); // Reset city when country changes
        }
    };

    const handleCityChange = (data) => {
        const value = data.value;
        if (value !== selectedCity) {
            setSelectedCity(value);
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setValue('dob', date);  // Update the form value
    };

    const handleDateInput = (e) => {
        const inputValue = e.target.value;
        const parsedDate = new Date(inputValue);

        // Check if the date is valid
        if (parsedDate instanceof Date && !isNaN(parsedDate)) {
            setSelectedDate(parsedDate);
            setValue('dob', parsedDate); 
        }
    };

    const handleKeyDown = (e) => {
        const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "-"];
        
        if (!/[\d/]/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    }; 

    const isValidDate = (date) => {
        return date && date !== "0000-00-00" && !isNaN(new Date(date).getTime());
    };

    const uploadImage = () => {
        fileRef.current.click();
    };

    const uploadImageEvent = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Generate a new file name based on the current timestamp and replace spaces with underscores
            const timestamp = new Date().getTime();
            const newFileName = `${timestamp}_${file?.name.replace(/\s+/g, '_')}`;

            // Create a new File object with the updated name
            const newFile = new File([file], newFileName, { type: file?.type });
            setFile(newFile);

            // Generate the image preview URL
            const imagePreviewUrl = URL.createObjectURL(newFile);
            setImagePreview(imagePreviewUrl);
        }
    };

    const uploadCertificateEvent = (e) => {
        const file = e.target.files[0];

        if (file) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                setGovCertiImgError(t("invalidImageType"));
                setGovCertificate('');
                setGovCertificatePreview(null); // Clear the preview
                return;
            }
            // Generate a new file name based on the current timestamp and replace spaces with underscores
            const timestamp = new Date().getTime();
            const newFileName = `${timestamp}_${file?.name.replace(/\s+/g, '_')}`;

            // Create a new File object with the updated name
            const newFile = new File([file], newFileName, { type: file?.type });
            setGovCertificate(newFile);
            setGovCertiImgError('');
            setGovCertificatePreview(URL.createObjectURL(file));
        }
    };

    const completeProfileSchema = Yup.object().shape({
        dob: Yup.date()
            .nullable()
            .notRequired()
            .typeError(t('valid', { field: t("date_of_birth") })),
        country_id: Yup.string().trim().nullable().notRequired(),
        city_id: Yup.string().trim().nullable().notRequired(),
        organization: Yup.string().matches(
            /^[a-zA-Z\s'-]*$/,
            t("fullNameValidation", { field: t("Organization_Name") }))
            .test('no-start-or-end-space', t('noStartEndSpace', { field: t("Organization_Name") }), validateNoStartOrEndSpace),
        experience: Yup.number()
            .transform((value, originalValue) => (originalValue.trim() === '' ? null : value))
            .typeError(t("experienceValidation"))
            .nullable()
            .notRequired()
            .min(0, t('minExp'))
            .max(50, t('maxExp')),
    });

    const formOptions = { resolver: yupResolver(completeProfileSchema) };
    const { register, handleSubmit, setValue, formState, setError } = useForm(formOptions);
    const { errors } = formState;

    const onSubmit = useCallback(
        async (values) => {

            if (selectedQuestion && !answer.trim()) {
                setError('answer', { type: 'manual', message: t("required", { field: t("answer") }) });
                return;
            }

            const imagedata = file ? await uploadFileToS3(file, "user_profile_image") : '';
            const govtCertifate = govCertificate ? await uploadFileToS3(govCertificate, "user_government_certificate") : "";
            if (imagedata) {
                values.image = imagedata.fileName
            } else {
                values.image = ""
            }

            if (govtCertifate) {
                values.government_certificate = govtCertifate.fileName
            } else {
                values.government_certificate = ""
            }

            // Convert the selected date to yyyy-mm-dd format
            values.dob = values.dob ?
                `${values.dob.getFullYear()}-${String(values.dob.getMonth() + 1).padStart(2, '0')}-${String(values.dob.getDate()).padStart(2, '0')}`
                : '';

            values.country_id = selectedCountry;
            values.city_id = selectedCity;
            values.security_question = selectedQuestion;
            values.security_answer = answer ? answer.trim() : "";
            // return console.log('values: ', values);

            const myPromise = new Promise((resolve, reject) => {
                dispatch(
                    completeUserProfile({
                        payload: {
                            ...values,
                        },
                    })
                )
                    .then((response) => {
                        console.log(response, "user res");
                        if (response.payload.code === 200) {
                            resolve(response.payload);
                            navigate("/home");
                        } else {
                            reject(response.payload);
                        }
                    });
            });
            showPromiseNotification(myPromise);
        },
        [dispatch, navigate, file, govCertificate, selectedCity, selectedCountry, selectedQuestion,answer]
    );

    const handleOrganizationChange = (e) => {
        const value = e.target.value;
        if (/[^a-zA-Z0-9\s'-]/.test(value)) {
            // Display error if emoji or invalid character is typed
            setError('organization', { type: 'manual', message: t("emojiNotAllowed") });
        }
    };

    return (
        <div className="signin-page-area">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12">
                        <form className="signin-inner" onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <div className="col-12">
                                    <div className="single-input-inner style-bg-border">
                                        <h4 className="SignInUpHeading text-center">
                                            {t('completeProfile')}
                                        </h4>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="EditProfileImgBox">
                                        <img
                                            src={imagePreview || process.env.PUBLIC_URL + "/assets/img/icon/default.jpg"}
                                            alt="EditProfileImg"
                                            id="EditProfileImg"
                                            className="EditProfileImg img-cover"
                                            onClick={uploadImage}
                                        />
                                        <div className="EditProfileInputBox">
                                            <img
                                                src={process.env.PUBLIC_URL + "/assets/img/icon/EditinputImg.svg"}
                                                alt="EditinputImg"
                                                className="EditinputImg img-cover"
                                            />
                                            <input
                                                type="file"
                                                name='image'
                                                ref={fileRef}
                                                className="EditProfileInputFile"
                                                id="EditProfileInputFile"
                                                accept="image/*"
                                                onChange={(e) => uploadImageEvent(e)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-4 col-xl-4 col-lg-6 col-md-12 col-sm-12 col-12 ">
                                    <div className="profileGride">
                                        <div className="ComplateProfileMain">
                                            <div className="InputBirDate mt-1">
                                                <div className="single-input-inner style-bg-border">
                                                    <DatePicker
                                                        selected={isValidDate(selectedDate) ? selectedDate : null}  // Default to no date selected
                                                        onChange={handleDateChange} // Call this function when a date is picked
                                                        onInput={handleDateInput}
                                                        onKeyDown={handleKeyDown}  
                                                        dateFormat="yyyy-MM-dd"  // Ensure the display format is yyyy-mm-dd
                                                        placeholderText={t('optionalDOB') + ": YYYY-MM-DD"}
                                                        maxDate={maxDate}
                                                        showYearDropdown
                                                        scrollableYearDropdown
                                                    />
                                                    <p className="text-danger">
                                                        {errors.dob?.message}
                                                    </p>
                                                </div>
                                                <div className="cloud">
                                                    <input
                                                        type="file"
                                                        className="form-control  apply-input-file"
                                                        placeholder={t('selectFieldName', { field: t('Subject') })}
                                                        id="field2"
                                                        accept="image/*"
                                                        name="government_certificate"
                                                        ref={fileCertificateRef}
                                                        onChange={(e) => uploadCertificateEvent(e)}
                                                    />
                                                    {govCertificatePreview ? (
                                                        <img src={govCertificatePreview} alt="Government Certificate"
                                                            style={{
                                                                maxWidth: '200px',
                                                                maxHeight: '200px',
                                                                borderRadius: '8px',
                                                                border: '1px solid #ddd'
                                                            }}
                                                        />
                                                    ) : (
                                                        <>
                                                            <img src={process.env.PUBLIC_URL + "/assets/img/icon/cloud.svg"} alt="cloud" className="cloud-img" />
                                                            <span className="upload-text">
                                                                {
                                                                    t("govCerti")
                                                                }
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                                <p className="text-danger">{govCertiImgError}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-4 col-xl-4 col-lg-6 col-md-12 col-sm-12 col-12">
                                    <div className="profileGride">
                                        <div className="ComplateProfileMain">
                                            <div className="SelectBox mt-4">
                                                <Select name="security_question" id="security_question"
                                                    placeholder={t("securityQue")}
                                                    defaultValue={selectedQuestion}
                                                    onChange={(data) => {
                                                        setSelectedQuestion(data.value)
                                                    }}
                                                    options={[
                                                        { value: 'q1', label: t("securityQue1") },
                                                        { value: 'q2', label: t("securityQue2") },
                                                        { value: 'q3', label: t("securityQue3") },
                                                        { value: 'q4', label: t("securityQue4") },
                                                    ]}
                                                    styles={reactSelectStyles}
                                                />
                                            </div>
                                            {selectedQuestion && (
                                                <div className="single-input-inner style-bg-border mt-4">
                                                    <input name='answer' type="text" placeholder={t('enterFieldName', { field: t("Answer") })}
                                                        onChange={(e) => {
                                                            setAnswer(e.target.value)
                                                        }}
                                                    />
                                                    <p className="text-danger">
                                                        {errors.answer?.message}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="SelectBox mt-4">
                                                <Select name="country_id" id="country_id"
                                                    placeholder={t('selectFieldName', { field: t('Country') })}
                                                    defaultValue={selectedCountry}
                                                    onChange={handleCountryChange}
                                                    options={
                                                        countries?.data?.map(country => ({ value: country.id, label: country.name }))
                                                    }
                                                    styles={reactSelectStyles}
                                                />
                                                <p className="text-danger">
                                                    {errors.country_id?.message}
                                                </p>
                                            </div>
                                            <div className="SelectBox mt-4">
                                                <Select name="city_id" id="city_id"
                                                    placeholder={t('selectFieldName', { field: t('City') })}
                                                    defaultValue={selectedCity}
                                                    onChange={handleCityChange}
                                                    options={
                                                        cities?.data?.map(country => ({ value: country.id, label: country.name }))
                                                    }
                                                    styles={reactSelectStyles}
                                                />
                                                <p className="text-danger">
                                                    {errors.city_id?.message}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-4 col-xl-4 col-lg-6 col-md-12 col-sm-12 col-12">
                                    <div className="profileGride pt-1">
                                        <div className="single-input-inner style-bg-border">
                                            <input name='organization' type="text" placeholder={t("optionalOrg")} {...register('organization')}
                                                onChange={(e) => {
                                                    handleOrganizationChange(e);
                                                }}
                                            />
                                            <p className="text-danger">
                                                {errors.organization?.message}
                                            </p>
                                        </div>
                                        <div className="single-input-inner style-bg-border">
                                            <input name='experience' type="text" placeholder={t("optionalExp")} {...register('experience')} />
                                            <p className="text-danger">
                                                {errors.experience?.message}
                                            </p>
                                        </div>
                                        <button type='submit' className="btn btn-base w-100 mt-4">
                                            {t('getStarted')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}
